import { Bucket } from '../types';
import { Group } from '../types/group';
import { isOverBudgetBucket } from './bucket-over-budget';

/**
 * Group-header rollup policy (design deep-dive, 2026-07).
 *
 * The header anchors on one right-aligned primary figure whose label follows
 * the group's shape, using the same vocabulary as the bucket rows
 * (Available / Expected / Surplus / Exceeded). Reserves surface as a sentence
 * suffix and only become the primary figure for pure fund groups with no
 * monthly budget at all.
 *
 * Expense groups read their primary figure from the rollover-aware available
 * balance, not from `expenseBudget - spent`: every surface beneath the header
 * (bucket rows, the over-budget badge) already carries rollover, so month-only
 * math made the header contradict its own children. The detail sentence and
 * the ring fill stay month-scoped — they answer "how is this month pacing?" —
 * while availability decides the ring's tone, so an over-pace month that
 * rollover still covers reads amber instead of red.
 */

export interface GroupRollupInput {
  /** Signed monthly outflow across BUDGET + SAVINGS buckets (positive = spent). */
  spent: number;
  /** Monthly budget across BUDGET + SAVINGS buckets. */
  expenseBudget: number;
  /** Signed income activity (negative in refund-heavy months). */
  received: number;
  incomeBudget: number;
  /**
   * Savings total: fund balances of goal-bearing buckets only. Every bucket
   * carries a rollover fund balance; a fund goal is what declares savings
   * intent, so goal-less rollover remainders never read as "saved".
   */
  fundTotal: number;
  /**
   * Rollover-aware available balance: fund balances of goal-LESS expense
   * buckets, the complement of `fundTotal` (no bucket is in both). Optional —
   * when omitted the rollup falls back to month-only `expenseBudget - spent`.
   */
  availableTotal?: number;
  /** Actual bucket presence, independent of monthly amounts and activity. */
  bucketCount?: number;
  /** At least one goal-less expense bucket participates in spending reports. */
  hasReportedAvailableBuckets?: boolean;
  /**
   * How many expense buckets the user excluded from spending reports. Those
   * buckets still count toward `availableTotal` but not toward `spent` /
   * `expenseBudget`, so the header has to say so or the two figures look wrong
   * together.
   */
  excludedCount?: number;
}

export type GroupRollupTone = 'neutral' | 'income' | 'warn' | 'danger';
export type GroupRingTone = 'ok' | 'warn' | 'danger';

export interface GroupRollup {
  figure: { value: string; label: string; tone: GroupRollupTone };
  detail: string;
  /** Reserve suffix rendered after the detail sentence, when reserves exist. */
  fundSuffix: { text: string; tone: 'neutral' | 'danger' } | null;
  /**
   * Quiet suffix naming how many buckets sit outside spending reports, rendered
   * after `fundSuffix` in the same muted line. Always neutral in tone — it
   * explains a gap between the figure and the sentence, it is not a warning.
   */
  excludedSuffix: string | null;
  /** 0-100 ring fill; null hides the ring fill entirely (pure fund / empty groups). */
  ringPercent: number | null;
  ringTone: GroupRingTone;
}

/** Everything the shape-specific rollups decide; the suffix is layered on after. */
type GroupRollupShape = Omit<GroupRollup, 'excludedSuffix'>;

const AMBER_PACE_THRESHOLD = 90;

export function categoryToRollupInput(category: Group): GroupRollupInput {
  const balance = category.balance ?? { INCOME: 0, BUDGET: 0 };
  const budgeted = category.budgeted ?? { INCOME: 0, BUDGET: 0 };

  return {
    // `balance`/`budgeted` already honor `excludedFromSpendingReports` — the server
    // serializer omits excluded buckets from those maps. Availability below does
    // not, because exclusion is reporting-only: the money is still spendable.
    spent: -((balance.BUDGET ?? 0) + (balance.SAVINGS ?? 0)),
    expenseBudget: (budgeted.BUDGET ?? 0) + (budgeted.SAVINGS ?? 0),
    received: balance.INCOME ?? 0,
    incomeBudget: budgeted.INCOME ?? 0,
    fundTotal: sumGoalBucketBalances(category.buckets ?? []),
    // Absent buckets mean availability is unknown, not zero — collapsing it to 0
    // would report "$0 available" instead of falling back to month math.
    availableTotal: category.buckets == null ? undefined : sumAvailableBucketBalances(category.buckets),
    bucketCount: category.buckets?.length,
    hasReportedAvailableBuckets: category.buckets?.some(bucket => isAvailableBucket(bucket) && !bucket.excludedFromSpendingReports),
    excludedCount: countExcludedExpenseBuckets(category.buckets ?? []),
  };
}

function sumGoalBucketBalances(buckets: readonly Bucket[]): number {
  return buckets
    .filter(bucket => bucket.fund?.goal != null)
    .reduce((sum, bucket) => sum + (bucket.fund?.balance ?? 0), 0);
}

function sumAvailableBucketBalances(buckets: readonly Bucket[]): number {
  return buckets
    .filter(isAvailableBucket)
    .reduce((sum, bucket) => sum + (bucket.fund?.balance ?? 0), 0);
}

function isAvailableBucket(bucket: Bucket): boolean {
  return (bucket.type === 'BUDGET' || bucket.type === 'SAVINGS') && bucket.fund?.goal == null;
}

function countExcludedExpenseBuckets(buckets: readonly Bucket[]): number {
  // Transfers are excluded by policy, not by choice, so naming them would put
  // "· 1 excluded" on every group that has one. Only user-set expense buckets count.
  return buckets.filter(
    bucket => (bucket.type === 'BUDGET' || bucket.type === 'SAVINGS') && bucket.excludedFromSpendingReports,
  ).length;
}

export function computeGroupRollup(input: GroupRollupInput): GroupRollup {
  return {
    ...rollupShape(input),
    excludedSuffix: excludedSuffix(input.excludedCount ?? 0),
  };
}

function rollupShape(input: GroupRollupInput): GroupRollupShape {
  // A side counts as present when it has a budget OR meaningful activity —
  // a group with an expense budget plus unbudgeted income activity (or vice
  // versa) must not hide that activity from the rollup.
  const incomeSide = input.incomeBudget > 0 || Math.abs(input.received) >= 1;
  const expenseSide = input.expenseBudget > 0 || Math.abs(input.spent) >= 1;

  if (incomeSide && expenseSide) return mixedRollup(input);
  if (input.incomeBudget > 0) return incomeRollup(input);
  if (input.expenseBudget > 0) return expenseRollup(input);
  // A $0 monthly plan can still hold reimbursement activity and carried money.
  // Availability alone cannot establish presence: empty/income groups also sum to $0.
  if (input.hasReportedAvailableBuckets) {
    return {
      ...expenseRollup(input),
      detail: input.spent > 0 ? `${moneyExact(input.spent)} spent · no monthly budget` : 'no monthly budget',
      ringPercent: null,
    };
  }
  if (input.spent > 0) return unbudgetedRollup(input);
  if (Math.abs(input.fundTotal) >= 1) return pureFundRollup(input);
  // Any empty-report group with exclusions routes here, $0 included: "no budgets
  // yet · 2 excluded" contradicts itself, and "$0 available" is the honest read.
  if ((input.excludedCount ?? 0) > 0 && input.availableTotal !== undefined) return excludedOnlyRollup(input);

  return {
    figure: { value: '—', label: '', tone: 'neutral' },
    detail: (input.bucketCount ?? 0) > 0 ? 'no monthly budget' : 'no budgets yet',
    fundSuffix: null,
    ringPercent: null,
    ringTone: 'ok',
  };
}

/** Matches the pre-existing header rule; income buckets never count as over. */
export function countOverBudgetBuckets(buckets: readonly Bucket[]): number {
  return buckets.filter(isOverBudgetBucket).length;
}

function mixedRollup(input: GroupRollupInput): GroupRollupShape {
  const net = input.received - input.spent;
  const plannedNet = input.incomeBudget - input.expenseBudget;
  const tone: GroupRollupTone = net < plannedNet ? 'danger' : net >= 0 ? 'income' : 'neutral';
  const inPart = input.incomeBudget > 0 ? `in ${money(input.received)} of ${money(input.incomeBudget)}` : `in ${money(input.received)}`;
  const outPart = input.expenseBudget > 0 ? `out ${money(input.spent)} of ${money(input.expenseBudget)}` : `out ${money(input.spent)}`;

  return {
    figure: { value: money(net), label: 'net', tone },
    detail: `${inPart} · ${outPart}`,
    fundSuffix: fundSuffix(input.fundTotal),
    ringPercent: input.expenseBudget > 0 ? percent(input.spent, input.expenseBudget) : null,
    ringTone: expenseRingTone(input.spent, input.expenseBudget, input.availableTotal),
  };
}

function incomeRollup(input: GroupRollupInput): GroupRollupShape {
  const received = Math.max(input.received, 0);
  const detail = `received ${money(input.received)} of ${money(input.incomeBudget)}`;
  const suffix = fundSuffix(input.fundTotal);

  if (received > input.incomeBudget) {
    return {
      figure: { value: `+${money(received - input.incomeBudget)}`, label: 'surplus', tone: 'income' },
      detail,
      fundSuffix: suffix,
      ringPercent: 100,
      ringTone: 'ok',
    };
  }
  if (received >= input.incomeBudget) {
    return {
      figure: { value: money(received), label: 'received', tone: 'income' },
      detail,
      fundSuffix: suffix,
      ringPercent: 100,
      ringTone: 'ok',
    };
  }
  return {
    // True (unclamped) received: a refund month raises what's still expected.
    figure: { value: money(input.incomeBudget - input.received), label: 'expected', tone: 'income' },
    detail,
    fundSuffix: suffix,
    ringPercent: percent(received, input.incomeBudget),
    ringTone: 'ok',
  };
}

function expenseRollup(input: GroupRollupInput): GroupRollupShape {
  const rollover = input.availableTotal !== undefined;
  const available = input.availableTotal ?? input.expenseBudget - input.spent;
  const figure =
    available < 0
      ? { value: money(-available), label: 'exceeded', tone: 'danger' as const }
      : { value: money(available), label: rollover ? 'available' : 'remaining', tone: 'neutral' as const };

  return {
    figure,
    detail: `${moneyExact(input.spent)} of ${money(input.expenseBudget)} spent`,
    fundSuffix: fundSuffix(input.fundTotal),
    ringPercent: percent(input.spent, input.expenseBudget),
    ringTone: expenseRingTone(input.spent, input.expenseBudget, input.availableTotal),
  };
}

function unbudgetedRollup(input: GroupRollupInput): GroupRollupShape {
  return {
    figure: { value: money(input.spent), label: 'unbudgeted', tone: 'warn' },
    detail: 'no budget set',
    fundSuffix: fundSuffix(input.fundTotal),
    ringPercent: 100,
    ringTone: 'warn',
  };
}

/**
 * Every expense bucket in the group is excluded from spending reports, so the
 * server-filtered `spent`/`expenseBudget` are both zero. The availability those
 * buckets carry is still real, spendable money — reporting "no budgets yet" over
 * bucket rows that show live balances hides it.
 */
function excludedOnlyRollup(input: GroupRollupInput): GroupRollupShape {
  const available = input.availableTotal ?? 0;

  return {
    figure:
      available < 0
        ? { value: money(-available), label: 'exceeded', tone: 'danger' }
        : { value: money(available), label: 'available', tone: 'neutral' },
    detail: 'nothing in spending reports',
    fundSuffix: fundSuffix(input.fundTotal),
    // No month budget to pace against, so the ring stays empty; tone still
    // follows availability so an overdrawn group reads red.
    ringPercent: null,
    ringTone: expenseRingTone(input.spent, input.expenseBudget, input.availableTotal),
  };
}

function pureFundRollup(input: GroupRollupInput): GroupRollupShape {
  if (input.fundTotal < 0) {
    return {
      figure: { value: money(input.fundTotal), label: 'overdrawn', tone: 'danger' },
      detail: 'reserves below zero',
      fundSuffix: null,
      ringPercent: null,
      ringTone: 'danger',
    };
  }
  return {
    figure: { value: money(input.fundTotal), label: 'saved', tone: 'neutral' },
    detail: 'no monthly budget',
    fundSuffix: null,
    ringPercent: null,
    ringTone: 'ok',
  };
}

function excludedSuffix(excludedCount: number): GroupRollup['excludedSuffix'] {
  if (excludedCount < 1) return null;
  return `· ${excludedCount} excluded`;
}

function fundSuffix(fundTotal: number): GroupRollup['fundSuffix'] {
  if (fundTotal >= 1) return { text: `· ${money(fundTotal)} saved`, tone: 'neutral' };
  if (fundTotal <= -1) return { text: `· ${money(fundTotal)} reserves`, tone: 'danger' };
  return null;
}

function expenseRingTone(spent: number, budget: number, availableTotal?: number): GroupRingTone {
  const pct = budget > 0 ? (spent / budget) * 100 : 0;
  if (availableTotal !== undefined) {
    if (availableTotal < 0) return 'danger';
    return pct >= AMBER_PACE_THRESHOLD ? 'warn' : 'ok';
  }
  if (pct > 100) return 'danger';
  if (pct >= AMBER_PACE_THRESHOLD) return 'warn';
  return 'ok';
}

function percent(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(Math.max(Math.round((part / whole) * 100), 0), 100);
}

function money(value: number): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  return value < 0 ? `-${formatted}` : formatted;
}

function moneyExact(value: number): string {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  return value < 0 ? `-${formatted}` : formatted;
}
